/**
 * Voltage Complementary Variants
 * Displays variant-specific product recommendations
 * @version 1.3.1
 */

(function() {
  'use strict';

  // ============================================================================
  // Configuration
  // ============================================================================

  const CONFIG = {
    SELECTORS: {
      BLOCK: '.voltage-complementary-variants',
      CONTENT: '.voltage-complementary-variants__content',
      LOADING: '.voltage-complementary-variants__loading',
      CAROUSEL: '.voltage-complementary-variants__carousel',
      NAV: '.voltage-complementary-variants__nav'
    },
    DEFAULTS: {
      LIMIT: 4,
      MIN_NAV_ITEMS: 3,
      DEBOUNCE_MS: 150
    }
  };

  // Track active fetches and timeouts
  const activeFetches = new Map();
  const debounceTimers = new Map();

  // Debug logging (set to false for production)
  const DEBUG = false;
  const log = DEBUG ? console.log.bind(console, 'Voltage:') : () => {};

  // ============================================================================
  // Prevent Multiple Initializations
  // ============================================================================

  // Remove any existing listener before adding new one
  if (window.voltageVariantChangeHandler) {
    log('Removing previous event listener');
    document.removeEventListener('variant:change', window.voltageVariantChangeHandler);
  }

  // Cancel any active fetches from previous instance
  if (window.voltageActiveFetches) {
    log('Cancelling previous fetches');
    window.voltageActiveFetches.forEach(token => {
      token.cancelled = true;
    });
    window.voltageActiveFetches.clear();
  }

  // Clear any debounce timers from previous instance
  if (window.voltageDebounceTimers) {
    log('Clearing previous timers');
    window.voltageDebounceTimers.forEach(timer => clearTimeout(timer));
    window.voltageDebounceTimers.clear();
  }

  // Store references globally so we can clean them up on next load
  window.voltageActiveFetches = activeFetches;
  window.voltageDebounceTimers = debounceTimers;

  // ============================================================================
  // Initialization
  // ============================================================================

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    try {
      log('Initializing complementary variants');
      initializeBlocks();
      setupEventListeners();
    } catch (error) {
      console.error('Voltage: Initialization error', error);
    }
  }

  function setupEventListeners() {
    // Store handler globally so it can be removed on next page load
    window.voltageVariantChangeHandler = handleVariantChange;
    document.addEventListener('variant:change', window.voltageVariantChangeHandler);
  }

  function handleVariantChange(event) {
    const variant = event.detail.variant;
    if (!variant) return;
    log('Variant changed to', variant.id);
    updateAllBlocksDebounced(variant);
  }

  // ============================================================================
  // Block Management
  // ============================================================================

  function initializeBlocks() {
    const blocks = document.querySelectorAll(CONFIG.SELECTORS.BLOCK);
    log('Found', blocks.length, 'blocks');

    blocks.forEach(block => {
      const { variantId, productId } = block.dataset;

      // Verify the block's product data exists in the current window context
      if (variantId && productId && window.voltageProductVariants?.[productId]) {
        loadBlock(block, productId, variantId);
      } else {
        log('Skipping block - missing data for product', productId);
      }
    });
  }

  function updateAllBlocksDebounced(variant) {
    // Clear existing debounce timer
    if (debounceTimers.has('variant-change')) {
      clearTimeout(debounceTimers.get('variant-change'));
    }
    
    // Set new debounce timer
    const timer = setTimeout(() => {
      updateAllBlocks(variant);
      debounceTimers.delete('variant-change');
    }, CONFIG.DEFAULTS.DEBOUNCE_MS);
    
    debounceTimers.set('variant-change', timer);
  }

  function updateAllBlocks(variant) {
    const blocks = document.querySelectorAll(CONFIG.SELECTORS.BLOCK);
    
    blocks.forEach(block => {
      const productId = block.dataset.productId;
      block.dataset.variantId = variant.id;
      loadBlock(block, productId, variant.id);
    });
  }

  async function loadBlock(block, productId, variantId) {
    const blockKey = `${productId}-${variantId}`;

    // Cancel previous fetch for this block if still running
    if (activeFetches.has(blockKey)) {
      log('Cancelling previous fetch for', blockKey);
      const oldToken = activeFetches.get(blockKey);
      oldToken.cancelled = true;
    }

    const fetchToken = { cancelled: false };
    activeFetches.set(blockKey, fetchToken);

    log('Loading block for product', productId, 'variant', variantId);

    const elements = getBlockElements(block);
    const limit = parseInt(block.dataset.limit) || CONFIG.DEFAULTS.LIMIT;

    showLoading(elements);

    const variantData = getVariantData(productId, variantId);

    if (!hasComplementaryVariants(variantData)) {
      log('No complementary variants found');
      hideBlock(block);
      activeFetches.delete(blockKey);
      return;
    }

    try {
      const products = await fetchProducts(variantData.complementary_variants, limit, fetchToken);

      // Check if cancelled during fetch
      if (fetchToken.cancelled) {
        log('Fetch was cancelled for', blockKey);
        activeFetches.delete(blockKey);
        return;
      }

      log('Loaded', products.length, 'products');

      if (products.length > 0) {
        renderProducts(elements.carousel, products);
        updateNavigation(elements.nav, products.length);
        showBlock(block, elements);
      } else {
        hideBlock(block);
      }
    } catch (error) {
      if (!fetchToken.cancelled) {
        console.error('Voltage: Error loading complementary products', error);
      }
      hideBlock(block);
    } finally {
      // Ensure cleanup happens even if cancelled
      if (activeFetches.get(blockKey) === fetchToken) {
        activeFetches.delete(blockKey);
      }
    }
  }

  // ============================================================================
  // Data Helpers
  // ============================================================================

  function getBlockElements(block) {
    return {
      content: block.querySelector(CONFIG.SELECTORS.CONTENT),
      loading: block.querySelector(CONFIG.SELECTORS.LOADING),
      carousel: block.querySelector(CONFIG.SELECTORS.CAROUSEL),
      nav: block.querySelector(CONFIG.SELECTORS.NAV)
    };
  }

  function getVariantData(productId, variantId) {
    const data = window.voltageProductVariants?.[productId]?.[variantId];
    if (!data) {
      log('No variant data found for', productId, variantId);
    }
    return data;
  }

  function hasComplementaryVariants(variantData) {
    return variantData?.complementary_variants?.length > 0;
  }

  // ============================================================================
  // Product Fetching
  // ============================================================================

  async function fetchProducts(variantData, limit, fetchToken) {
    const products = [];
    const variantsToFetch = variantData.slice(0, limit);

    log('Fetching', variantsToFetch.length, 'products');

    for (let i = 0; i < variantsToFetch.length; i++) {
      const variantInfo = variantsToFetch[i];

      // Check cancellation before each fetch
      if (fetchToken.cancelled) {
        log('Fetch cancelled, stopping');
        break;
      }

      try {
        const product = await fetchSingleProduct(variantInfo, fetchToken);
        if (product && !fetchToken.cancelled) {
          products.push(product);
        }
      } catch (error) {
        if (!fetchToken.cancelled) {
          console.error('Voltage: Error fetching product', variantInfo, error);
        }
      }

      // Add small delay between fetches to work around Chrome 132 libxml bug
      // This gives the browser time to process the response before next fetch
      if (i < variantsToFetch.length - 1 && !fetchToken.cancelled) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }

    return products;
  }

  async function fetchSingleProduct(variantInfo, fetchToken) {
    const { product_handle, id } = variantInfo;
    if (!product_handle) return null;

    // Add timeout to prevent hanging fetches
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout

    try {
      const response = await fetch(`/products/${product_handle}.js`, {
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok || fetchToken.cancelled) return null;

      const product = await response.json();

      if (fetchToken.cancelled) return null;

      product.selected_variant = product.variants.find(v => v.id === id);

      return product;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        log('Fetch aborted/timeout for', product_handle);
      }
      return null;
    }
  }

  // ============================================================================
  // Rendering
  // ============================================================================

  function renderProducts(carousel, products) {
    if (!carousel) return;

    carousel.innerHTML = '';

    // Use DocumentFragment for better performance
    const fragment = document.createDocumentFragment();
    products.forEach(product => {
      fragment.appendChild(createProductCard(product));
    });

    carousel.appendChild(fragment);
  }

  // Cache color scheme to avoid repeated DOM queries
  let cachedColorScheme = null;

  function getColorScheme() {
    if (cachedColorScheme) return cachedColorScheme;

    const existingModal = document.querySelector('quick-buy-modal');
    cachedColorScheme = existingModal
      ? existingModal.className.match(/color-scheme--[\w-]+/)?.[0] || 'color-scheme--scheme-1'
      : 'color-scheme--scheme-1';

    return cachedColorScheme;
  }

  function createProductCard(product) {
    const variant = product.selected_variant || product.variants[0];
    const featuredMedia = variant.featured_image || product.featured_image;
    const container = document.createElement('div');

    // Build image HTML matching theme's product-cross-sell structure
    let imageHtml = '';
    if (featuredMedia) {
      const imageUrl = `${featuredMedia.src}&width=300`;
      const srcset = `${featuredMedia.src}&width=150 150w, ${featuredMedia.src}&width=300 300w, ${featuredMedia.src}&width=450 450w`;
      imageHtml = `
        <a href="${product.url}">
          <img src="${imageUrl}"
               srcset="${srcset}"
               sizes="150px"
               alt="${escapeHtml(featuredMedia.alt || product.title)}"
               loading="lazy"
               class="constrained-image"
               style="--image-max-width: 150px">
        </a>
      `;
    }

    // Build price HTML
    const priceHtml = typeof Shopify !== 'undefined' && Shopify.formatMoney
      ? Shopify.formatMoney(variant.price)
      : formatMoney(variant.price);

    // Build quick-buy button HTML matching theme structure
    let buttonHtml = '';
    if (product.available) {
      if (product.variants.length === 1) {
        // Single variant - direct add to cart using product-form component
        buttonHtml = `
          <product-form>
            <form method="post" action="/cart/add">
              <input type="hidden" name="id" value="${variant.id}">
              <button type="submit" class="block link subheading subheading-xs">Add to cart</button>
            </form>
          </product-form>
        `;
      } else {
        // Multiple variants - use quick-buy modal
        const quickBuyId = 'product-quick-buy-voltage-' + product.id;
        const colorSchemeClass = getColorScheme();
        buttonHtml = `
          <button type="button" aria-controls="${quickBuyId}" class="link subheading subheading-xs">Choose options</button>
          <quick-buy-modal id="${quickBuyId}" product-url="${product.url}" class="modal modal--quick-buy color-scheme ${colorSchemeClass}"></quick-buy-modal>
        `;
      }
    }

    // Build complete card HTML matching product-cross-sell structure
    container.innerHTML = `
      <div class="product-cross-sell product-cross-sell--vertical snap-start">
        ${imageHtml}
        <div class="v-stack gap-2">
          <div class="v-stack gap-0.5">
            <a href="${product.url}" class="subheading">${escapeHtml(product.title)}</a>
            <div class="price-list">
              <span class="price text-subdued">${priceHtml}</span>
            </div>
          </div>
          ${buttonHtml}
        </div>
      </div>
    `;

    return container.firstElementChild;
  }

  // ============================================================================
  // UI State Management
  // ============================================================================

  function showLoading(elements) {
    if (elements.loading) elements.loading.style.display = 'block';
    if (elements.content) elements.content.style.display = 'none';
  }

  function showBlock(block, elements) {
    if (elements.content) elements.content.style.display = 'block';
    if (elements.loading) elements.loading.style.display = 'none';
    block.style.display = 'block';
  }

  function hideBlock(block) {
    block.style.display = 'none';
  }

  function updateNavigation(nav, productCount) {
    if (!nav) return;
    nav.style.display = productCount >= CONFIG.DEFAULTS.MIN_NAV_ITEMS ? 'flex' : 'none';
  }

  // ============================================================================
  // Utility Functions
  // ============================================================================

  function formatMoney(cents) {
    const dollars = Math.floor(cents / 100);
    return '$' + dollars.toLocaleString('en-US');
  }

  function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

})();